import { Chrome, Smartphone, Monitor } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Returns the appropriate icon component based on user agent string
 */
export const getIcon = (ua: string): LucideIcon => {
    if (ua.toLowerCase().includes("chrome")) {
        return Chrome;
    } else if (ua.toLowerCase().includes("mobile") || ua.toLowerCase().includes("iphone") || ua.toLowerCase().includes("android")) {
        return Smartphone;
    } else {
        return Monitor;
    }
};

/**
 * Parses user agent string into browser, OS, and device information
 */
/**
 * Parses normalized UA data from format:
 * BrowserName, BrowserVersion, OSName, OSVersion
 * Example: "Chrome,143.0.0.0,macOS,10.15.7"
 */
export const parseUserAgent = (ua: string) => {
    const parts = ua.split(",").map(p => p.trim());

    // Default fallback
    if (parts.length < 4) {
        return {
            browser: ua,
            os: "Unknown OS",
            device: "Unknown Device"
        };
    }

    const [browserName, browserVersion, osName, osVersion] = parts;

    // ------------------------------
    // Device Detection Based on osName
    // ------------------------------
    let device = "Unknown Device";

    const osLower = osName.toLowerCase();

    // macOS → MacBook
    if (osLower === "macos") {
        // Attempt Apple Silicon inference from version (Big Sur+)
        const major = parseInt(osVersion.split(".")[0]);
        const isAppleSilicon = major >= 11;
        device = isAppleSilicon ? "MacBook (Apple Silicon)" : "MacBook (Intel)";
    }

    // Windows
    else if (osLower.includes("windows")) {
        device = "Windows PC";
    }

    // iPhone / iPad
    else if (osLower.includes("ios")) {
        device = "iPhone";
    } else if (osLower.includes("ipados")) {
        device = "iPad";
    }

    // Android
    else if (osLower.includes("android")) {
        device = "Android Device";
    }

    // Linux
    else if (osLower.includes("linux")) {
        device = "Linux Device";
    }

    return {
        browser: `${browserName} ${browserVersion}`,
        os: `${osName} ${osVersion}`,
        device
    };
};


/**
 * Formats a timestamp into a human-readable relative time string
 */
export const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};

export const formatTimestampIST = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }) + " IST";
  };  

/**
 * Fetches location information for an IP address using ipwho.is API
 * Results are cached in localStorage for 72 hours
 * @param ipString - IP address string (may contain multiple IPs separated by commas)
 * @returns Location string or null if unable to fetch
 */
export const fetchIpLocation = async (ipString: string): Promise<string | null> => {
    // Handle cases like "client, proxy1, proxy2"
    const ip = ipString.split(",")[0].trim();

    if (ip === "127.0.0.1" || ip === "::1") return "Localhost";

    const CACHE_KEY = `ip_location_${ip}`;
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

    // --- LocalStorage Cache Check ---
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
        try {
            const { location, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
                return location;
            }
        } catch {
            localStorage.removeItem(CACHE_KEY);
        }
    }

    // Helper to cache results
    const saveToCache = (location: string) => {
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ location, timestamp: Date.now() })
        );
    };

    // -------------------------------
    // 1️⃣ TRY GEOJS API (NO SIGNUP, CORS OK)
    // -------------------------------
    try {
        const res = await fetch(`https://get.geojs.io/v1/ip/geo/${ip}.json`);
        if (res.ok) {
            const data = await res.json();

            const parts = [data.city, data.region, data.country].filter(Boolean);
            const location = parts.join(", ");

            if (location) {
                saveToCache(location);
                return location;
            }
        }
    } catch (e) {
        console.warn("GeoJS lookup failed:", e);
    }

    // -------------------------------
    // 2️⃣ FALLBACK → IPLOCATION.tools
    // -------------------------------
    try {
        const res = await fetch(`https://api.iplocation.net/?ip=${ip}`);
        if (res.ok) {
            const data = await res.json();

            const parts = [
                data.city,
                data.region,
                data.country_name || data.country
            ].filter(Boolean);

            const location = parts.join(", ");

            if (location) {
                saveToCache(location);
                return location;
            }
        }
    } catch (e) {
        console.warn("IPLocation.tools lookup failed:", e);
    }

    return null;
};

